// components/tour/SpotlightTour.jsx
//
// Guided "spotlight" product tour. Dims the screen and cuts out / highlights one
// real on-screen element at a time, with the KB mascot + a callout and
// Back/End/Next controls. Ported from swa-botfieldPM's SpotlightTour.tsx (custom,
// no library) and adapted for rprs: JS + CSS, role-filtered steps passed in by
// <TourHost>, and a `drive` directive (via tourStore) that asks the active page
// to open a modal/panel on the seeded demo data instead of botfieldPM's
// openCoach/openTray.
//
// Targets are `data-tour="…"` attributes (or existing stable classes). Several
// render only after navigation/an async open, so we navigate then poll for the
// target, fall back to a secondary selector, then to a centered callout.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { tourStore } from './tourStore';
import './SpotlightTour.css';

const MASCOT = '/assets/kb-navigator/frame-1.png';
const PAD = 6;                 // halo padding around the highlighted element
const CALLOUT_W = 320;
const FIND_TIMEOUT_MS = 5000;  // how long to wait for an async/driven target
const POLL_MS = 120;
// Grace window before a step's `fallback` is allowed to match. The real target
// (a modal/sidebar/tab opened by a `drive` directive or navigation) often renders
// a beat after the step begins, while the fallback (e.g. the grid root) is already
// present. Without this delay the fallback wins the race and the step highlights
// the wrong element. The primary target is still matched the instant it appears.
const FALLBACK_GRACE_MS = 1200;
const COLUMN_SCROLL_PX = 1.5;  // gentle auto-scroll for column steps

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Nearest scrollable ancestor (for column auto-scroll). null → scroll window.
function getScrollParent(el) {
  let node = el?.parentElement || null;
  while (node) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 4) return node;
    node = node.parentElement;
  }
  return null;
}

export default function SpotlightTour({ steps = [], onClose }) {
  const navigate = useNavigate();

  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null); // callout anchor for extraTarget (union) steps
  const [pulseRects, setPulseRects] = useState([]);   // extra attention rings (step.pulse); don't affect the hole/callout
  const [extraHoleRects, setExtraHoleRects] = useState([]); // additional lit cutouts (step.extraHoles) → SVG-mask dim
  const [centered, setCentered] = useState(false);
  const targetElRef = useRef(null);
  const calloutRef = useRef(null);
  const [calloutH, setCalloutH] = useState(190);

  const step = steps[index];
  const last = index >= steps.length - 1;

  const finish = useCallback(() => onClose && onClose(), [onClose]);

  // Nothing to show (e.g. all steps filtered out) — bail.
  useEffect(() => { if (steps.length === 0) finish(); }, [steps.length, finish]);

  // Tell the active page which modal/panel to open for this step (or none).
  // Equal directives are a no-op in the store, so the modal stays open without
  // flicker across consecutive sub-steps that share a directive.
  useEffect(() => { tourStore.setDrive(step?.drive ?? null); }, [index, step?.drive]);
  useEffect(() => () => tourStore.setDrive(null), []);

  // Find (and keep measuring) the current step's target. Navigates first for page
  // steps, then polls for the element, then falls back to a centered callout.
  useEffect(() => {
    if (!step) return;

    let cancelled = false;
    let poll = null;
    let backstop = null;
    let scrollTO = null;
    let scrollRAF = 0;
    const deadline = Date.now() + FIND_TIMEOUT_MS;

    setRect(null);
    setAnchorRect(null);
    setPulseRects([]);
    setExtraHoleRects([]);
    setCentered(false);
    targetElRef.current = null;
    const fallbackAfter = Date.now() + FALLBACK_GRACE_MS;

    if (step.route) navigate(step.route);

    // Intro/summary steps with no target render centered immediately (no poll wait).
    if (!step.target && !step.fallback) {
      setCentered(true);
      return () => { cancelled = true; };
    }

    const measure = () => {
      const el = targetElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Extra attention rings — independent of the main hole + callout placement.
      if (step.pulse) {
        const sels = Array.isArray(step.pulse) ? step.pulse : [step.pulse];
        setPulseRects(
          sels.map((s) => document.querySelector(s)).filter(Boolean).map((e) => e.getBoundingClientRect()),
        );
      }
      // Extra lit cutouts (rendered via the SVG-mask dim alongside the primary hole).
      if (step.extraHoles) {
        const sels = Array.isArray(step.extraHoles) ? step.extraHoles : [step.extraHoles];
        setExtraHoleRects(
          sels.map((s) => document.querySelector(s)).filter(Boolean).map((e) => e.getBoundingClientRect()),
        );
      }
      if (step.column) {
        const h = Math.max(r.height, window.innerHeight - r.top - 16);
        setRect(new DOMRect(r.left, r.top, r.width, h));
        return;
      }
      if (step.extraTarget) {
        const extra = document.querySelector(step.extraTarget);
        if (extra) {
          const er = extra.getBoundingClientRect();
          const left = Math.min(r.left, er.left);
          const top = Math.min(r.top, er.top);
          const right = Math.max(r.right, er.right);
          const bottom = Math.max(r.bottom, er.bottom);
          setRect(new DOMRect(left, top, right - left, bottom - top));
          setAnchorRect(er);
          return;
        }
      }
      setRect(r);
    };

    const onFound = (el) => {
      targetElRef.current = el;
      if (step.column) {
        const scroller = getScrollParent(el);
        if (scroller) scroller.scrollTop = 0; else window.scrollTo(0, 0);
        measure();
        if (!prefersReducedMotion()) {
          scrollTO = setTimeout(() => {
            let pos = scroller ? scroller.scrollTop : window.scrollY;
            const advance = () => {
              const max = scroller
                ? scroller.scrollHeight - scroller.clientHeight
                : document.documentElement.scrollHeight - window.innerHeight;
              if (pos < max - 1) {
                pos += COLUMN_SCROLL_PX;
                if (scroller) scroller.scrollTop = pos; else window.scrollTo(0, pos);
                scrollRAF = requestAnimationFrame(advance);
              }
            };
            scrollRAF = requestAnimationFrame(advance);
          }, 800);
        }
        return;
      }
      if (step.extraTarget) {
        const scroller = getScrollParent(el);
        if (scroller) scroller.scrollTop = 0; else window.scrollTo(0, 0);
        measure();
        backstop = setTimeout(measure, 250);
        return;
      }
      // Side-placed steps (e.g. a modal): don't scroll the page — the element is
      // already positioned (fixed/centered). Just measure the box; the callout sits
      // beside it. `autoScroll` gently reveals a tall element's own inner content.
      if (step.placement === 'side' || step.autoScroll) {
        // If the target lives inside a scroll container (e.g. a section deep in the
        // modal), bring it into view within that container first. No-op when the
        // target IS the scroller (a whole modal) — that stays put and autoScroll runs.
        const scroller = getScrollParent(el);
        if (scroller && scroller !== el) {
          el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
        measure();
        backstop = setTimeout(measure, 300);
        if (step.autoScroll && !prefersReducedMotion()) {
          const container = document.querySelector(step.autoScroll);
          if (container) {
            scrollTO = setTimeout(() => {
              container.scrollTop = 0;
              let pos = 0;
              const advance = () => {
                const max = container.scrollHeight - container.clientHeight;
                if (pos < max - 1) {
                  pos = Math.min(pos + 3, max);
                  container.scrollTop = pos;
                  scrollRAF = requestAnimationFrame(advance);
                }
              };
              scrollRAF = requestAnimationFrame(advance);
            }, 700);
          }
        }
        return;
      }
      measure();
      el.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      backstop = setTimeout(measure, 420);
    };

    const tryFind = () => {
      const primary = step.target ? document.querySelector(step.target) : null;
      // Only consult the fallback after the grace window (so a late-rendering target
      // isn't beaten by an always-present fallback). Steps with no target at all use
      // the fallback immediately.
      const graceReady = !step.target || Date.now() >= fallbackAfter;
      const fb = (!primary && step.fallback && graceReady) ? document.querySelector(step.fallback) : null;
      const el = primary || fb;
      if (el) {
        if (poll) { clearInterval(poll); poll = null; }
        if (!cancelled) onFound(el);
        return;
      }
      if (Date.now() > deadline) {
        if (poll) { clearInterval(poll); poll = null; }
        if (!cancelled) setCentered(true); // graceful fallback — explain it centered
      }
    };

    tryFind();
    if (!targetElRef.current) poll = setInterval(tryFind, POLL_MS);

    const onViewport = () => measure();
    window.addEventListener('scroll', onViewport, true);
    window.addEventListener('resize', onViewport);

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (backstop) clearTimeout(backstop);
      if (scrollTO) clearTimeout(scrollTO);
      if (scrollRAF) cancelAnimationFrame(scrollRAF);
      window.removeEventListener('scroll', onViewport, true);
      window.removeEventListener('resize', onViewport);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, step?.target]);

  // Esc ends the tour.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') finish(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  // Measure the callout once laid out, so positioning clamps against real height.
  useLayoutEffect(() => {
    if (calloutRef.current) setCalloutH(calloutRef.current.offsetHeight);
  }, [index, step?.title, step?.body, rect]);

  if (!step) return null;

  const goNext = () => { if (last) finish(); else setIndex((i) => i + 1); };
  const goBack = () => setIndex((i) => Math.max(0, i - 1));

  const columnStep = !!step.column;
  const showHole = !!rect && !centered;

  let calloutStyle;
  if (showHole && rect && step.placement === 'side') {
    // Sit in the side margin (vertically centered), so it never covers the element.
    const top = Math.max(
      16,
      Math.min((window.innerHeight - calloutH) / 2, window.innerHeight - calloutH - 12),
    );
    const rightRoom = window.innerWidth - rect.right;
    const leftRoom = rect.left;
    const SIDE_GAP = 32; // clearance between the callout and the highlighted panel
    if (rightRoom >= CALLOUT_W + SIDE_GAP) {
      calloutStyle = { left: rect.right + SIDE_GAP, top };
    } else if (leftRoom >= CALLOUT_W + SIDE_GAP) {
      calloutStyle = { left: Math.max(12, rect.left - SIDE_GAP - CALLOUT_W), top };
    } else {
      // Neither margin fits (narrow viewport) — hug the roomier side, clamped.
      const left = rightRoom >= leftRoom
        ? Math.min(rect.right + SIDE_GAP, window.innerWidth - CALLOUT_W - 12)
        : Math.max(12, rect.left - SIDE_GAP - CALLOUT_W);
      calloutStyle = { left, top };
    }
  } else if (showHole && rect && columnStep) {
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - CALLOUT_W / 2),
      window.innerWidth - CALLOUT_W - 12,
    );
    calloutStyle = { left, top: 16 };
  } else if (showHole && rect && anchorRect) {
    const rightLeft = anchorRect.right + 16;
    if (rightLeft + CALLOUT_W <= window.innerWidth - 12) {
      // Room to the right of the anchor — sit beside it.
      const top = Math.max(16, Math.min(anchorRect.top - 6, window.innerHeight - calloutH - 12));
      calloutStyle = { left: rightLeft, top };
    } else {
      // No room right (e.g. a top-right element) — drop below the union rect, clamped.
      const belowTop = rect.bottom + 14;
      const top = belowTop + calloutH < window.innerHeight ? belowTop : Math.max(12, rect.top - 14 - calloutH);
      const left = Math.min(
        Math.max(12, rect.left + rect.width / 2 - CALLOUT_W / 2),
        window.innerWidth - CALLOUT_W - 12,
      );
      calloutStyle = { left, top };
    }
  } else if (showHole && rect) {
    const belowTop = rect.bottom + 14;
    const fitsBelow = belowTop + calloutH < window.innerHeight;
    const top = fitsBelow ? belowTop : Math.max(12, rect.top - 14 - calloutH);
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - CALLOUT_W / 2),
      window.innerWidth - CALLOUT_W - 12,
    );
    calloutStyle = { left, top };
  } else {
    calloutStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  }

  // Multi-hole steps (step.extraHoles) can't use the single box-shadow cutout — two
  // box-shadows would dim each other's holes. Render an SVG-mask dim + flat outlines.
  const multiHole = showHole && !!rect && extraHoleRects.length > 0;
  const holeRects = multiHole ? [rect, ...extraHoleRects] : [];

  return createPortal(
    <>
      <div
        className={`tour-catcher${showHole ? '' : ' tour-catcher--dim'}`}
        onMouseDown={(e) => e.preventDefault()}
      />
      {showHole && rect && !multiHole && (
        <div
          className="tour-hole"
          style={{
            left: rect.left - PAD,
            top: rect.top - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      )}
      {multiHole && (
        <svg className="tour-dim-svg" width="100%" height="100%" aria-hidden="true">
          <defs>
            <mask id="tour-hole-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {holeRects.map((h, i) => (
                <rect key={i} x={h.left - PAD} y={h.top - PAD} width={h.width + PAD * 2} height={h.height + PAD * 2} rx="10" ry="10" fill="black" />
              ))}
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0, 40, 52, 0.72)" mask="url(#tour-hole-mask)" />
        </svg>
      )}
      {multiHole && holeRects.map((h, i) => (
        <div
          key={i}
          className="tour-hole tour-hole--flat"
          style={{ left: h.left - PAD, top: h.top - PAD, width: h.width + PAD * 2, height: h.height + PAD * 2 }}
        />
      ))}
      {showHole && anchorRect && (
        <div
          className="tour-pulse"
          style={{
            left: anchorRect.left - 6,
            top: anchorRect.top - 6,
            width: anchorRect.width + 12,
            height: anchorRect.height + 12,
          }}
        />
      )}
      {showHole && pulseRects.map((pr, i) => (
        <div
          key={i}
          className="tour-pulse"
          style={{ left: pr.left - 6, top: pr.top - 6, width: pr.width + 12, height: pr.height + 12 }}
        />
      ))}
      <div className="tour-callout" ref={calloutRef} style={calloutStyle} role="dialog" aria-label="Feature tour">
        <div className="tour-callout-head">
          <img className="tour-mascot" src={MASCOT} alt="" />
          <div className="tour-title">{step.title}</div>
        </div>
        <p className="tour-body">{step.body}</p>
        <div className="tour-controls">
          <span className="tour-counter">Step {index + 1} of {steps.length}</span>
          <button className="tour-back" onClick={goBack} disabled={index === 0}>← Back</button>
          <button className="tour-end" onClick={finish}>End</button>
          <button className="tour-next" onClick={goNext}>{last ? 'Finish ✓' : 'Next →'}</button>
        </div>
      </div>
    </>,
    document.body,
  );
}
